import React, { useState, useEffect } from 'react'
import { T } from '../ui'
import FuturesTab from './FuturesTab'
import FantasyTab from './FantasyTab'
import TrendsTab from './TrendsTab'
import PickemTab from './PickemTab'
import MarketPulseTab from './MarketPulseTab'
import GameLinesTab from './GameLinesTab'
import MyBoardTab from './MyBoardTab'
import LadderTab from './LadderTab'
import SimCheck from '../simcheck'
import { track } from '../track'

// More — everything that isn't one of the main doors, in one list. Tap a tool to open it
// full-screen, "‹ More" to come back. Tools with `go` live as their own full view in the app
// (they navigate there instead of opening here). Locks/Fades now sit on This Week as board
// lenses, and the TD screens live under the Touchdowns tab.
export const TOOLS = [
  { id: 'record', icon: '📑', title: 'Record', desc: 'Every graded play, the season record and calibration', go: 'record' },
  { id: 'myboard', icon: '🧩', title: 'My Board', desc: 'Build your own dashboard — pick & reorder anything in the app' },
  { id: 'sheets', icon: '🔥', title: 'Matchup Sheets', desc: 'Softest defenses this week + who faces them', go: 'sheets' },
  { id: 'ladder', icon: '🪜', title: 'Ladder Spotter', desc: 'Yardage spots set up to climb — model % at each rung' },
  { id: 'blowout', icon: '💥', title: 'Blowout Spots', desc: 'Big favorites + the backup RB angle', go: 'blowout' },
  { id: 'pickem', icon: '🎯', title: 'Pick’em +EV', desc: 'PrizePicks / Underdog lines vs. the model' },
  { id: 'pulse', icon: '📊', title: 'Market Pulse', desc: 'Prop line movement + with/against the model' },
  { id: 'gamelines', icon: '💰', title: 'Game Lines', desc: 'Moneyline / spread / total — movement + sharp money' },
  { id: 'futures', icon: '🏆', title: 'Futures', desc: 'Season-long player & team lines' },
  { id: 'fantasy', icon: '🧮', title: 'Fantasy', desc: 'Rankings, start/sit, optimizer, waivers' },
  { id: 'trends', icon: '📈', title: 'Trends', desc: "This week's risers & fallers" },
  { id: 'community', icon: '🏟️', title: 'Community', desc: 'Wall · leaderboard · requests roadmap', go: 'community' },
  { id: 'sim', icon: '🎲', title: 'Sim Check', desc: 'Correlated slate simulation vs. the board', pro: true },
]

export default function MoreTab({ season, week, onPlayer, onAddLeg, slipKeys, pro, onRefer, onNavigate, gameFilter, initial = null }) {
  const [view, setView] = useState(initial)
  useEffect(() => { setView(initial) }, [initial])
  const tools = TOOLS.filter(t => !t.pro || pro)   // Pro-only tools (Sim Check) hidden in Simple

  if (view) {
    const tool = TOOLS.find(t => t.id === view)   // any tool, incl. Pro-only ones opened from My Board
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px 8px' }}>
          <button onClick={() => setView(null)} style={{ background: T.card, border: `1px solid ${T.border}`,
            color: T.green, borderRadius: 9, padding: '7px 12px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>‹ More</button>
          <span style={{ fontSize: 15, fontWeight: 800 }}>{tool?.icon} {tool?.title}</span>
        </div>
        {view === 'myboard' && <MyBoardTab season={season} week={week} onNavigate={onNavigate} onRefer={onRefer} />}
        {view === 'ladder' && <LadderTab season={season} week={week} onPlayer={onPlayer} />}
        {view === 'pickem' && <PickemTab season={season} week={week} />}
        {view === 'pulse' && <MarketPulseTab season={season} week={week} />}
        {view === 'gamelines' && <GameLinesTab season={season} week={week} />}
        {view === 'futures' && <FuturesTab season={season} onPlayer={onPlayer} />}
        {view === 'fantasy' && <FantasyTab season={season} week={week} onPlayer={onPlayer} />}
        {view === 'trends' && <TrendsTab season={season} week={week} onPlayer={onPlayer} />}
        {view === 'sim' && <SimCheck season={season} week={week} />}
      </div>
    )
  }

  return (
    <div style={{ padding: '14px 16px 80px' }}>
      {/* Refer & Earn — top of More so users find their $20 code without hunting. */}
      {onRefer && (
        <button onClick={onRefer} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
          cursor: 'pointer', background: `linear-gradient(90deg, ${T.greenDim}, ${T.card})`, border: `1px solid ${T.greenMid}`,
          borderRadius: 12, padding: '13px 14px', color: T.text, marginBottom: 16 }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, background: T.card2, display: 'grid', placeItems: 'center', fontSize: 17, flexShrink: 0 }}>🎁</span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 800 }}>Refer &amp; Earn — get $20</span>
            <span style={{ display: 'block', fontSize: 11.5, color: T.muted }}>Your code + link · $20 for every 5 friends who stay a month</span>
          </span>
          <span style={{ color: T.green, fontSize: 18 }}>›</span>
        </button>
      )}
      <div style={{ fontSize: 12, fontWeight: 800, color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Deeper tools</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {tools.map(t => (
          <button key={t.id} onClick={() => { track('tool', t.id); t.go ? onNavigate && onNavigate(t.go) : setView(t.id) }} style={{ display: 'flex', alignItems: 'center', gap: 12,
            width: '100%', textAlign: 'left', cursor: 'pointer', background: T.card, border: `1px solid ${T.border}`,
            borderRadius: 12, padding: '13px 14px', color: T.text }}>
            <span style={{ width: 34, height: 34, borderRadius: 9, background: T.card2, display: 'grid', placeItems: 'center', fontSize: 17, flexShrink: 0 }}>{t.icon}</span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span style={{ display: 'block', fontSize: 14, fontWeight: 800 }}>{t.title}</span>
              <span style={{ display: 'block', fontSize: 11.5, color: T.muted }}>{t.desc}</span>
            </span>
            <span style={{ color: T.faint, fontSize: 18 }}>›</span>
          </button>
        ))}
      </div>
      <div style={{ fontSize: 10, color: T.faint, marginTop: 14, lineHeight: 1.5 }}>
        Locks &amp; Fades are now on This Week (Most likely / Unders). TD Scorers, TD Matchups, Pay Dirt and Due are under Touchdowns. Informational only · 21+.
      </div>
    </div>
  )
}
